import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BookOpen, Award, MessageCircle, Key, UserPlus, ExternalLink, Star } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const dropdownRef = useRef(null);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      // silent
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      // silent
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      // silent
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'new_book':
        return <BookOpen className="h-4 w-4 text-blue-400" />;
      case 'badge_earned':
        return <Award className="h-4 w-4 text-yellow-400" />;
      case 'chat_activity':
      case 'new_chat':
        return <MessageCircle className="h-4 w-4 text-cyan-400" />;
      case 'new_review':
        return <Star className="h-4 w-4 text-yellow-400" />;
      case 'password_change':
        return <Key className="h-4 w-4 text-orange-400" />;
      case 'user_created':
        return <UserPlus className="h-4 w-4 text-purple-400" />;
      default:
        return <Bell className="h-4 w-4 text-gray-400" />;
    }
  };

  const getRouteUrl = (notif) => {
    // Use explicit route_url if available
    if (notif.route_url) return notif.route_url;
    // Fallback dynamic routing based on type
    switch (notif.type) {
      case 'new_book':
        return notif.book_id ? `/book/${notif.book_id}` : '/';
      case 'badge_earned':
        return '/library';
      case 'chat_activity':
      case 'new_chat':
        return notif.book_id ? `/book/${notif.book_id}` : '/community';
      case 'new_review':
        return notif.book_id ? `/book/${notif.book_id}` : '/';
      case 'password_change':
      case 'user_created':
        return '/admin/users';
      default:
        return null;
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id);
    const routeUrl = getRouteUrl(notif);
    if (routeUrl) {
      setIsOpen(false);
      navigate(routeUrl);
    } else {
      setExpandedId(prev => prev === notif.id ? null : notif.id);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (!token) return null;

  return (
    <div className="relative" ref={dropdownRef} style={{ zIndex: 9999 }}>
      {/* Bell Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-300 hover:text-white transition-colors cursor-pointer"
        data-testid="notification-bell"
        type="button"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Popup */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl shadow-2xl overflow-hidden border border-zinc-700/80 animate-in fade-in slide-in-from-top-2 duration-200"
          style={{
            zIndex: 9999,
            backgroundColor: '#111113',
            boxShadow: '0 20px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.05)'
          }}
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800" style={{ backgroundColor: '#18181b' }}>
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-red-500" />
              <h3 className="text-white font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-red-400 hover:text-red-300 cursor-pointer transition-colors"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-6 text-center">
                <div className="animate-spin h-6 w-6 border-2 border-red-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="text-zinc-500 text-xs mt-2">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
                <p className="text-zinc-500 text-sm">No notifications yet</p>
                <p className="text-zinc-600 text-xs mt-1">Updates will appear here</p>
              </div>
            ) : (
              notifications.map((notif, idx) => {
                const routeUrl = getRouteUrl(notif);
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotificationClick(notif)}
                    className={`px-4 py-3 border-b border-zinc-800/60 hover:bg-zinc-800/50 cursor-pointer transition-all ${
                      !notif.is_read ? 'bg-red-500/5 border-l-2 border-l-red-500' : ''
                    } ${expandedId === notif.id ? 'bg-zinc-800/70' : ''}`}
                    style={{ animationDelay: `${idx * 30}ms` }}
                    data-testid="notification-item"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className={`text-sm font-medium ${expandedId === notif.id ? 'text-white' : notif.is_read ? 'text-zinc-400' : 'text-white'} ${expandedId === notif.id ? '' : 'truncate'}`}>
                            {notif.title}
                          </p>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {routeUrl && (
                              <ExternalLink className="h-3 w-3 text-zinc-500" />
                            )}
                            {!notif.is_read && (
                              <span className="h-2 w-2 bg-red-500 rounded-full"></span>
                            )}
                          </div>
                        </div>

                        {/* Collapsed: single line preview */}
                        {expandedId !== notif.id && (
                          <>
                            <p className="text-xs text-zinc-500 mt-0.5 truncate">{notif.message}</p>
                            <p className="text-[10px] text-zinc-600 mt-1">{formatTime(notif.created_at)}</p>
                          </>
                        )}

                        {/* Expanded: full content */}
                        {expandedId === notif.id && (
                          <div className="mt-2 bg-zinc-900/80 rounded-lg p-3 border border-zinc-700/50">
                            <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{notif.message}</p>
                            <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-700/50">
                              <p className="text-[10px] text-zinc-500">{new Date(notif.created_at).toLocaleString()}</p>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${notif.is_read ? 'bg-zinc-700 text-zinc-400' : 'bg-red-500/20 text-red-400'}`}>
                                {notif.is_read ? 'Read' : 'New'}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-zinc-800 text-center" style={{ backgroundColor: '#18181b' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="text-xs text-zinc-400 hover:text-white transition-colors cursor-pointer"
              data-testid="close-notifications-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
